import {
  ButtonERP,
  DataTablePrimeReact,
  PaginationERP,
  TableERP,
} from "@finanedi.es/storybook"
import { faPaperPlane } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { GetEmpresasGestionadasAsesoriaByFiltersDtoProps } from "api/DTOs/GetEmpresasGestionadasAsesoriaByFiltersDto"
import {
  GetEmpresasGestionadasAsesoriaByFilters,
  GetEmpresasGestionadasAsesoriaByFiltersQueryParamsProps,
  deleteEmpresaGestionadaAsesoriaById,
} from "api/EmpresaGestionadaAsesoria.service"
import { SortingCriteria } from "api/interfaces/SortingCriteria"
import notification from "common/utils/notification"
import { PAGINATION_STYLES } from "constants/primereactStyles"
import { AnimatePresence } from "framer-motion"
import { useFilterMenu } from "hooks/useFilterMenu"
import { Skeleton } from "primereact/skeleton"
import { useCallback, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { ModalEliminarEmpresa } from "../components/ModalEliminarEmpresa"
import ModalInvitarEmpresaGestionada from "../components/ModalInvitarEmpresaGestionada/ModalInvitarEmpresaGestionada"
import { EMPRESAS_GESTIONADAS_COLUMNS } from "../constants/empresasGestionadasColumns"
import { FilterMenu } from "./FilterMenu"

export const EmpresasGestionadas = () => {
  const { t } = useTranslation()
  const [openDeleteModal, setOpenDeleteModal] = useState<{
    show: boolean
    company: GetEmpresasGestionadasAsesoriaByFiltersDtoProps | undefined
  }>({
    show: false,
    company: undefined,
  })
  const [visibleMailEnviar, setVisibleMailEnviar] = useState(false)
  const fetchEmpresasGestionadas = useCallback(
    () =>
      (
        queryParams: GetEmpresasGestionadasAsesoriaByFiltersQueryParamsProps
      ) => {
        return GetEmpresasGestionadasAsesoriaByFilters({
          ...queryParams,
          SkipCount: queryParams.SkipCount ?? 0,
          MaxResultCount: queryParams.MaxResultCount ?? 10,
        })
      },
    []
  )
  const [sortingCriteria, setSortingCriteria] = useState<SortingCriteria>({
    sort: undefined,
    property: undefined,
  })
  const [pagination, setPagination] = useState({
    itemsPerPage: 100,
    skipCount: 0,
  })
  const [sortField, setSortField] = useState<string | undefined>(undefined)
  const [sortOrder, setSortOrder] = useState<1 | 0 | -1 | null | undefined>(0)

  const dtRef = useRef<DataTablePrimeReact<$TSMigrationFixMe[]>>(null)
  const handleResetPagination = () => {
    dtRef.current && dtRef.current?.resetScroll()
    setPagination({
      itemsPerPage: pagination.itemsPerPage,
      skipCount: 0,
    })
  }

  const { items, isLoading, totalCount, setServiceQuery, fetchTableData } =
    useFilterMenu({
      serviceCall: fetchEmpresasGestionadas,
    })

  const handleConfirmDelete = async (id: string) => {
    try {
      await deleteEmpresaGestionadaAsesoriaById(id)
      await fetchTableData()
      notification.success()
      setOpenDeleteModal({ show: false, company: undefined })
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
      setOpenDeleteModal({ show: false, company: undefined })
    }
  }

  const onSort = ({
    sortField,
    sortOrder,
  }: {
    sortField: string | undefined
    sortOrder: 1 | 0 | -1 | null | undefined
  }) => {
    setSortingCriteria({
      property: sortField ?? undefined,
      sort: sortOrder === 1 ? "ASC" : sortOrder === -1 ? "DESC" : undefined,
    })
    setSortField(sortField)
    setSortOrder(sortOrder)
  }

  return (
    <>
      <div className='tw-flex tw-flex-col tw-w-full tw-shadow-3x2 tw-bg-simple-10 tw-py-3.5 tw-px-8 tw-m-px sm:tw-flex-row sm:tw-justify-between sm:tw-flex-wrap tw-gap-2'>
        <div className='tw-text-xl tw-font-normal tw-flex tw-items-center tw-text-nowrap'>
          {t("empresasGestionadas.header.title")} (
          {isLoading ? (
            <Skeleton className='!tw-h-5 !tw-w-8 !tw-mx-1' />
          ) : (
            <span>{totalCount}</span>
          )}
          )
        </div>
        <div className='tw-flex tw-flex-col sm:tw-flex-row sm:tw-justify-start sm:tw-flex-wrap tw-gap-1 lg:tw-gap-3'>
          <FilterMenu
            serviceQuery={setServiceQuery}
            sortingCriteria={sortingCriteria}
            maxResultCount={pagination.itemsPerPage}
            skipCount={pagination.skipCount}
            resetPagination={handleResetPagination}
          />
          <div className='tw-flex tw-flex-row tw-gap-1 lg:tw-gap-3'>
            <ButtonERP
              customize={{
                size: "md",
                type: "secondary",
              }}
              onClick={() => setVisibleMailEnviar(!visibleMailEnviar)}
              icon={<FontAwesomeIcon icon={faPaperPlane} />}
              label={`${t("empresasGestionadas.header.invite")}`}
            />
          </div>
        </div>
      </div>

      <div className='tw-flex tw-flex-col tw-h-[calc(100%-70px)] tw-relative '>
        <div className='tw-h-[calc(100%-60px)] tw-min-h-0'>
          <TableERP
            refTable={dtRef}
            columns={EMPRESAS_GESTIONADAS_COLUMNS(t, setOpenDeleteModal)}
            className='tw-h-full'
            columnResizeMode='expand'
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={onSort}
            removableSort
            value={items}
            showGridlines
            dataKey='id'
            lazy
            scrollable
            size='small'
            loading={isLoading}
            emptyMessage={t("primeReact.table.emptyMessage")}
          />
        </div>
        <PaginationERP
          className={PAGINATION_STYLES}
          totalRecords={totalCount}
          first={pagination.skipCount}
          rows={pagination.itemsPerPage}
          resetScroll={() => dtRef.current && dtRef.current?.resetScroll()}
          setPagination={setPagination}
          optionDropdown={[10, 20, 50, 100]}
          texts={{
            textRowsPerPageDropdown: `${t("pagination.textRowsPag")}`,
            textCurrentPageReport: `${t("pagination.from")}`,
          }}
        />
      </div>

      <AnimatePresence>
        <ModalEliminarEmpresa
          isOpen={openDeleteModal.show}
          selectedCompany={openDeleteModal.company}
          onCancel={() =>
            setOpenDeleteModal({ show: false, company: undefined })
          }
          onConfirm={handleConfirmDelete}
        />
        <ModalInvitarEmpresaGestionada
          visible={visibleMailEnviar}
          onHide={() => {
            fetchTableData()
            setVisibleMailEnviar(!visibleMailEnviar)
          }}
        />
      </AnimatePresence>
    </>
  )
}
